<template>
    <div class="main">
        <div id="Nav" class="left" data-login>
            <div class="back"><button @click="goBack()" class="button icon fastic-circle-left" :aria-label="localizer.get('App.Common.PageBack')"></button></div>
            <div class="content">
                <h1 class="maintitle s60" v-html="localizer.get('Account.Login.MyBeghelliTitle')"></h1>
                <div class="desc">{{ localizer.get("Account.Login.MyBeghelliDesc") }}</div>
                <template v-if="options && options.externalProviders.length !== 0">
                    <div class="buttons">
                        <div class="cta secondary s16" data-color="white"><a :href="routes.Account_User_Register + (returnUrl ? '?returnurl=' + returnUrl : '')" class="button">{{ localizer.get("Account.Login.CreateProfile") }}</a></div>
                        <form :action="routes.Account_External_ExternalLogin + (returnUrl ? '?returnurl=' + returnUrl : '')" method="post" class="external">
                            <fieldset>
                                <div class="or">{{ localizer.get("App.Common.Or") }}</div>
                                <div v-for="(provider, index) in options.externalProviders" :key="index" class="cta secondary s16 icon" data-color="white"><button type="submit" name="provider" :value="provider.name" class="button"><img :src="'/images/' + provider.name + '.svg'" width="20" height="20" class="icon" :alt="provider.name">{{ localizer.get("Account.ExternalRegister.Login", provider.name) }}</button></div>
                            </fieldset>
                            <input type="hidden" name="__RequestVerificationToken" :value="formHelper.getRequestVerificationToken()" />
                        </form>
                    </div>
                </template>
            </div>
            <app-footer></app-footer>
        </div>
        <main id="Main" class="right">
            <div class="logo" data-color="cyan50">
                <a :href="routes.Home"><img src="/images/logo.svg" width="125" height="35" :alt="localizer.get('App.Common.Beghelli')" /></a>
            </div>
            <div v-if="isLoginVisible" @keydown.enter.prevent.stop="submitForm()" class="content">
                <div class="intro">
                    <h2 class="maintitle s36">{{ localizer.get("Account.Login.LoginTitle") }}</h2>
                </div>
                <div ref="form" class="form">
                    <fieldset>
                        <div class="item">
                            <label for="UserName">{{ localizer.get("Account.Common.EmailOrUserName") }}</label>
                            <input type="text" id="UserName" v-model.lazy="formData.userName" :class="[{ 'input-validation-error': formHelper.isControlValidationError('UserName', validationErrors) }, 'input w300']" />
                        </div>
                        <div v-if="isEmailNotConfirmed()" class="desc small"><a :href="routes.Account_User_EmailConfirmSend">{{ localizer.get("Account.Common.EmailConfirmation_Send") }}</a></div>
                        <div class="item">
                            <label for="Password">{{ localizer.get("Account.Common.Password") }}</label>
                            <div class="custominput" data-type="passwordremind">
                                <input type="password" aria-label="Password" id="Password" v-model.lazy="formData.password" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Password', validationErrors) }, 'input w300']" />
                                <button :class="[{ 'fastic-eye-show': isHidden, 'fastic-eye-hide': !isHidden }, 'showhide']" aria-label="Mostra password" @click="formHelper.showPassword('Password'), showPasswordIcon()"></button>
                                <a :href="routes.Account_User_PasswordRemind" class="link" data-type="remind">{{ localizer.get("Account.Common.Login_ForgotPassword") }}</a>
                            </div>
                        </div>
                    </fieldset>
                    <fieldset>
                        <div class="item">
                            <div class="checkbox">
                                <input id="IsPersistent" type="checkbox" v-model.lazy="formData.isPersistent" />
                                <label for="IsPersistent">{{ localizer.get("Account.Login.StayLogged") }}</label>
                                <div :data-tooltip="localizer.get('Account.Login.StayLoggedTooltip')" data-size="400"><span class="icon fastic-support" aria-hidden="true"></span></div>
                            </div>
                        </div>
                        <div v-if="validationErrors.length !== 0" class="validation-summary-errors">
                            <ul>
                                <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                            </ul>
                        </div>
                        <div class="cta"><button :class="[{ 'loading': isLoading }, 'button']" @click="submitForm()"><template v-if="!isLoading">{{ localizer.get("Account.Common.Login_Button") }}</template></button></div>
                    </fieldset>
                </div>
            </div>
            <div v-if="isTwoFactorVisible" class="content">
                <div class="intro">
                    <h2 class="maintitle s36">{{ localizer.get("Account.Common.TwoFactorCode") }}</h2>
                    {{ localizer.get("Account.Common.TwoFactorCode_Description") }}
                </div>
                <div @keydown.enter.prevent.stop="submitTwoFactor()" class="form">
                    <fieldset :disabled="isLoading">
                        <div class="item" data-label="top">
                            <div class="custominput" data-type="two-factor" :aria-label="localizer.get('Account.Common.TwoFactorCode')">
                                <input v-for="(item,index) in codeInputArray" :key="index" type="text" ref="codeInputs" :class="[{ 'input-validation-error': formHelper.isControlValidationError('Code', validationErrors) }, 'input w55']" maxlength="1" :data-index="item" />
                                <input type="text" class="textbox hidden-input" id="Code" v-model.lazy="twoFactorData.code" maxlength="6" tabindex="-1"/>
                            </div>
                            <button v-if="isTwoFactorRetryVisible" @click="sendTwoFactorCode()" class="link label-link small">{{ localizer.get("Account.Common.TwoFactorCode_Send") }}</button>
                        </div>
                        <div v-if="validationErrors.length !== 0" class="item validation-summary-errors">
                            <ul>
                                <li v-for="(validationError, index) in validationErrors" :key="index">{{ validationError.message }}</li>
                            </ul>
                        </div>
                        <div class="item">
                            <div class="cta">
                                <button :class="[{ 'loading': isLoading }, 'button']" @click="submitTwoFactor()">
                                    <template v-if="!isLoading">{{ localizer.get("Account.Common.Login_Button") }}</template>
                                </button>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </div>
        </main>
    </div>
</template>

<script setup lang="ts">
import { ref, nextTick, onBeforeMount } from "vue";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import Localizer from "@Scripts/Common/Localizer";

const props = defineProps<{ returnUrl: string }>();

const localizer = Localizer;
const formHelper = FormHelper;
const routes = (window as any).App.routes;

const isLoading = ref(false);
const isSuccess = ref(false);
const isLoginVisible = ref(false);
const isTwoFactorVisible = ref(false);
const isTwoFactorRetryVisible = ref(false);
const options = ref<any>(null);
const formData = ref<any>({ isPersistent: false });
const twoFactorData = ref<any>({ code: null });
const codeInputArray = ref([0, 1, 2, 3, 4, 5]);
const codeInputs = ref<HTMLInputElement[]>([]);
const validationErrors = ref<any[]>([]);
const isHidden = ref(true);

onBeforeMount(async () => {
    options.value = (await Axios.post(routes.Account_User_GetOptions, null, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
    isLoginVisible.value = true;
});

async function submitForm() {
    FormHelper.removeFocus();
    validationErrors.value = [];
    isLoading.value = true;
    try {
        let result = (await Axios.post(routes.Account_User_Login, formData.value, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
        if (!result.isTwoFactorRequired) {
            isSuccess.value = true;
            if (result.redirectUrl && props.returnUrl) {
                result.redirectUrl = result.redirectUrl + '?returnUrl=' + props.returnUrl;
            }
            window.location.href = result.redirectUrl ? result.redirectUrl : (props.returnUrl ? props.returnUrl : "/");
        } else {
            await sendTwoFactorCode();
            isLoginVisible.value = false;
            isTwoFactorVisible.value = true;

            nextTick(() => {
                inputTwoFactorCode();
            });
        }
    } catch (error: any) {
        formData.value.password = null;
        if (error.response && error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }
    isLoading.value = false;
}

async function submitTwoFactor() {
    FormHelper.removeFocus();
    validationErrors.value = [];
    isLoading.value = true;

    twoFactorData.value.isPersistent = formData.value.isPersistent;

    try {
        let result = (await Axios.post(routes.Account_User_TwoFactorValidate, twoFactorData.value, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } })).data;
        isSuccess.value = true;

        if (result.redirectUrl && props.returnUrl) {
            result.redirectUrl = result.redirectUrl + "?returnUrl=" + props.returnUrl;
        }

        window.location.href = result.redirectUrl ? result.redirectUrl : (props.returnUrl ? props.returnUrl : "/");
    } catch (error: any) {
        isLoading.value = false;
        twoFactorData.value.code = null;

        if (error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }
}

async function sendTwoFactorCode() {
    FormHelper.removeFocus();
    validationErrors.value = [];
    isLoading.value = true;
    isTwoFactorRetryVisible.value = false;

    try {
        await Axios.post(routes.Account_User_TwoFactorSend, null, { headers: { "RequestVerificationToken": FormHelper.getRequestVerificationToken() } });

        setTimeout(() => {
            isTwoFactorRetryVisible.value = true;
        }, 5000);
    } catch (error: any) {
        twoFactorData.value.code = null;

        if (error.response.status === 400) {
            validationErrors.value = FormHelper.getValidationErrors(error.response.data);
        }
    }

    isLoading.value = false;
}

function inputTwoFactorCode() {
    const inputs = codeInputs.value;

    inputs.forEach((input, index) => {
        input.addEventListener("input", (e: Event) => {
            const target = e.target as HTMLInputElement;
            target.value = target.value.replace(/\D/g, "");
            target.classList.remove("error");
            updateHiddenInput();
            if (target.value && index < inputs.length - 1) {
                inputs[index + 1].focus();
            }
        });

        input.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Backspace" && !input.value && index > 0) {
                inputs[index - 1].focus();
            }
        });

        input.addEventListener("paste", (e: ClipboardEvent) => {
            e.preventDefault();
            const pasteData = e.clipboardData?.getData("text") || "";
            const digits = pasteData.replace(/\D/g, "");

            if (digits.length === inputs.length) {
                inputs.forEach((input, i) => {
                    input.value = digits[i] || "";
                });
                updateHiddenInput();
                inputs[inputs.length - 1].focus();
            }
        });
    });

    function updateHiddenInput(): void {
        twoFactorData.value.code = Array.from(inputs).map(input => input.value).join("");
    }
}

function isEmailNotConfirmed(): boolean {
    return (validationErrors.value.length !== 0 && validationErrors.value.some((x: any) => x.message === localizer.get("Account.Common.Login_EmailNotConfirmed")));
}

function showPasswordIcon() {
    isHidden.value = !isHidden.value;
}

function goBack() {
    window.location.href = routes.Home;
}
</script>